import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner, Card, Table, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { codeSolutionGenerator, executeCode, dbEngine } from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";

const CodeSolutionGenerator = ({ problemStatement, language, version, testCases, userId, fileUrl, requestMode, bloomLevel, onAccept, onClose }) => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [randomMessage, setRandomMessage] = useState('');
  const [solutionData, setSolutionData] = useState(null);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [harnessTab, setHarnessTab] = useState('starter');
  const [schemaFileName, setSchemaFileName] = useState('');
  const randomMessageIntervalRef = useRef(null);

  const parseSchema = (schemaText) => {
    if (!schemaText) return { tables: [], inserts: [], raw: "" };
    try {
      const tables = [];
      const inserts = [];

      const splitByCommasAtParenDepth0 = (text) => {
        const parts = [];
        let current = "";
        let depth = 0;
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          if (char === '(') depth++;
          else if (char === ')') depth--;

          if (char === ',' && depth === 0) {
            parts.push(current.trim());
            current = "";
          } else {
            current += char;
          }
        }
        if (current.trim()) {
          parts.push(current.trim());
        }
        return parts;
      };

      const splitValues = (str) => {
        const result = [];
        let current = "";
        let inSingleQuote = false;
        let inDoubleQuote = false;
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          if (char === "'" && !inDoubleQuote) {
            inSingleQuote = !inSingleQuote;
          } else if (char === '"' && !inSingleQuote) {
            inDoubleQuote = !inDoubleQuote;
          }

          if (char === ',' && !inSingleQuote && !inDoubleQuote) {
            result.push(current.trim().replace(/^['"]|['"]$/g, ''));
            current = "";
          } else {
            current += char;
          }
        }
        if (current.trim()) {
          result.push(current.trim().replace(/^['"]|['"]$/g, ''));
        }
        return result;
      };

      const createRegex = /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(\w+)\s*\(([\s\S]*?)\);/gi;
      let match;
      while ((match = createRegex.exec(schemaText)) !== null) {
        const tableName = match[1];
        const innerContent = match[2].trim();
        const lines = splitByCommasAtParenDepth0(innerContent);

        const columns = [];
        const primaryKeys = [];
        const foreignKeys = {};

        lines.forEach(line => {
          const cleanLine = line.trim().toUpperCase();
          if (cleanLine.startsWith("PRIMARY KEY")) {
            const pkMatch = /\(([^)]+)\)/.exec(line);
            if (pkMatch) {
              pkMatch[1].split(',').forEach(c => primaryKeys.push(c.trim().toLowerCase()));
            }
          } else if (cleanLine.startsWith("FOREIGN KEY")) {
            const fkMatch = /FOREIGN\s+KEY\s*\(([^)]+)\)\s*REFERENCES\s*([^(]+)\(([^)]+)\)/i.exec(line);
            if (fkMatch) {
              const fkCol = fkMatch[1].trim().toLowerCase();
              const refTable = fkMatch[2].trim();
              const refCol = fkMatch[3].trim();
              foreignKeys[fkCol] = `${refTable}(${refCol})`;
            }
          }
        });

        lines.forEach(line => {
          const cleanLine = line.trim();
          const cleanLineUpper = cleanLine.toUpperCase();
          if (
            cleanLineUpper.startsWith("PRIMARY KEY") ||
            cleanLineUpper.startsWith("FOREIGN KEY") ||
            cleanLineUpper.startsWith("CONSTRAINT") ||
            cleanLineUpper.startsWith("UNIQUE")
          ) {
            return;
          }

          const spaceIdx = cleanLine.indexOf(' ');
          if (spaceIdx === -1) return;

          const colName = cleanLine.substring(0, spaceIdx).trim();
          const rest = cleanLine.substring(spaceIdx).trim();

          let type = "";
          let restOfRest = "";
          const openParenIdx = rest.indexOf('(');
          if (openParenIdx !== -1) {
            const closeParenIdx = rest.indexOf(')', openParenIdx);
            if (closeParenIdx !== -1) {
              type = rest.substring(0, closeParenIdx + 1).trim();
              restOfRest = rest.substring(closeParenIdx + 1).trim();
            }
          }

          if (!type) {
            const nextSpace = rest.indexOf(' ');
            if (nextSpace === -1) {
              type = rest;
              restOfRest = "";
            } else {
              type = rest.substring(0, nextSpace).trim();
              restOfRest = rest.substring(nextSpace).trim();
            }
          }

          const colNameLower = colName.toLowerCase();
          const isPrimary = primaryKeys.includes(colNameLower) || restOfRest.toUpperCase().includes("PRIMARY KEY");
          const isForeign = !!foreignKeys[colNameLower];
          const foreignRef = foreignKeys[colNameLower] || "";

          let otherConstraints = restOfRest.replace(/PRIMARY\s+KEY/gi, '').trim();

          columns.push({
            name: colName,
            type,
            isPrimary,
            isForeign,
            foreignRef,
            otherConstraints
          });
        });

        tables.push({
          name: tableName,
          columns
        });
      }

      const insertRegex = /INSERT\s+INTO\s+(\w+)\s*(?:\(([^)]+)\))?\s*VALUES\s*([\s\S]+?);/gi;
      while ((match = insertRegex.exec(schemaText)) !== null) {
        const tableName = match[1];
        const cols = match[2] ? match[2].split(',').map(c => c.trim()) : [];
        let valsBlock = match[3].trim();

        const rowsRaw = valsBlock.split(/\),\s*\(/);
        const dataRows = rowsRaw.map(rowStr => {
          let clean = rowStr.trim();
          if (clean.startsWith('(')) clean = clean.substring(1);
          if (clean.endsWith(')')) clean = clean.substring(0, clean.length - 1);
          return splitValues(clean);
        });

        inserts.push({
          table: tableName,
          columns: cols,
          rows: dataRows
        });
      }

      return { tables, inserts, raw: schemaText };
    } catch (e) {
      console.error("Error parsing schema:", e);
      return { tables: [], inserts: [], raw: schemaText };
    }
  };

  // Random messages to display during solution generation
  const randomMessages = [
    "Analyzing the problem structure...",
    "Designing the optimal algorithm...",
    "Writing efficient code...",
    "Optimizing for performance...",
    "Checking edge cases...",
    "Refining the solution...",
    "Validating the approach...",
    "Crafting the perfect solution...",
    "Testing different strategies...",
    "Ensuring code quality...",
    "Applying best practices...",
    "Fine-tuning the implementation...",
    "Reviewing code patterns...",
    "Enhancing readability...",
    "Building robust logic...",
  ];

  // Language mapping to match CodeEditor.js
  const languageMap = {
    javascript: "javascript",
    python: "python",
    'c++': "g++",
    cpp: "g++",
    java: "java",
    "c#": "csharp",
    csharp: "csharp",
    c: "c",
  };

  const languageOptions = {
    javascript: { fileExtension: ".js" },
    python: { fileExtension: ".py" },
    cpp: { fileExtension: ".cpp" },
    java: { fileExtension: ".java" },
    csharp: { fileExtension: ".cs" },
    c: { fileExtension: ".c" },
  };

  const defaultVersions = {
    javascript: "18.15.0",
    python: "3.10.0",
    cpp: "10.2.0",
    java: "15.0.2",
    csharp: "5.0.201",
    c: "10.2.0"
  };

  const getNormalizedLanguage = (lang) => {
    const normalized = lang?.toLowerCase()?.trim();
    if (!normalized) return "python";
    if (normalized === "c++" || normalized === "cpp" || normalized === "g++") return "cpp";
    if (normalized === "c#" || normalized === "csharp") return "csharp";
    if (normalized === "javascript" || normalized === "js") return "javascript";
    if (normalized === "python" || normalized === "py") return "python";
    if (normalized === "java") return "java";
    if (normalized === "c") return "c";
    return normalized;
  };

  const mergeSolutionAndHarness = (solution, harness, language) => {
    if (!harness) return solution.trim();

    const normLang = getNormalizedLanguage(language);
    const solText = solution.trim();
    const harText = harness.trim();

    if (normLang !== "java" && normLang !== "csharp") {
      return `${solText}\n\n${harText}`;
    }

    const solLines = solText.split('\n');
    const harLines = harText.split('\n');

    const directiveLines = [];
    const otherSolLines = [];
    const otherHarLines = [];

    const isDirectiveLine = (line) => {
      const trimmed = line.trim();
      if (normLang === "java") {
        return trimmed.startsWith("import ") || trimmed.startsWith("package ");
      }
      if (normLang === "csharp") {
        return trimmed.startsWith("using ") && trimmed.endsWith(";") && !trimmed.includes("(");
      }
      return false;
    };

    for (const line of solLines) {
      if (isDirectiveLine(line)) {
        if (!directiveLines.includes(line)) {
          directiveLines.push(line);
        }
      } else {
        otherSolLines.push(line);
      }
    }

    for (const line of harLines) {
      if (isDirectiveLine(line)) {
        if (!directiveLines.includes(line)) {
          directiveLines.push(line);
        }
      } else {
        otherHarLines.push(line);
      }
    }

    const combinedDirectives = directiveLines.join('\n');
    const combinedSol = otherSolLines.join('\n');
    const combinedHar = otherHarLines.join('\n');

    let result = "";
    if (combinedDirectives) {
      result += combinedDirectives + "\n\n";
    }

    if (normLang === "java") {
      result += combinedHar + "\n\n" + combinedSol;
    } else {
      result += combinedSol + "\n\n" + combinedHar;
    }

    return result.trim();
  };

  const fetchSolution = async (signal) => {
    if (!problemStatement || !language) {
      setError('Problem statement and language are required to generate a solution.');
      return;
    }

    // Clear any existing interval
    if (randomMessageIntervalRef.current) {
      clearInterval(randomMessageIntervalRef.current);
      randomMessageIntervalRef.current = null;
    }

    setIsLoading(true);
    setError('');
    setSolutionData(null);
    setTestResults([]);
    setLoadingMessage('Thinking...');
    setRandomMessage('');

    // Function to get a random message
    const getRandomMessage = () => {
      return randomMessages[Math.floor(Math.random() * randomMessages.length)];
    };

    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (fileUrl) {
        setLoadingMessage('Analyzing document context...');
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setLoadingMessage('Generating solution...');

      // Start showing random messages every 2.5 seconds
      setRandomMessage(getRandomMessage());
      randomMessageIntervalRef.current = setInterval(() => {
        setRandomMessage(getRandomMessage());
      }, 2500);

      const payload = {
        type: 'solution',
        user_id: userId,
        language: language.toLowerCase(),
        version: version,
        problem_statement: problemStatement,
        request_mode: requestMode || 'all',
        bloom_level: bloomLevel?.toLowerCase() || 'apply',
        test_cases: testCases.filter(tc => tc.testInput && tc.expectedOutput).map(tc => ({
          input: tc.testInput,
          output: tc.expectedOutput
        })),
        file_url: fileUrl,
        signal: signal
      };

      console.log('Sending solution generation payload:', payload);

      const response = await codeSolutionGenerator(payload, userId);

      if (response.solution) {
        if (typeof response.solution === 'string') {
          // Flat response: solution, harness, test_cases are all top-level keys
          setSolutionData({
            solution: response.solution,
            harness: response.harness || null,
            starter_code: response.starter_code || null,
            test_cases: response.test_cases || [],
            schema: response.schema || null
          });
        } else if (typeof response.solution === 'object') {
          // Nested response: everything is under response.solution
          setSolutionData({
            solution: response.solution.solution,
            harness: response.solution.harness || null,
            starter_code: response.solution.starter_code || null,
            test_cases: response.solution.test_cases || [],
            schema: response.solution.schema || response.schema || null
          });
        }
        setLoadingMessage('');
        setRandomMessage('');
      } else {
        throw new Error('Invalid response format: Solution missing.');
      }
    } catch (err) {
      // Extract error message with better handling
      let errorMessage = 'Oops! Something went wrong while generating the solution. Give it another shot! 🚀';

      // Check for HTTP status codes
      if (err.response) {
        const status = err.response.status;
        const responseData = err.response.data;

        const customMsg = responseData?.error || responseData?.message || responseData?.detail;

        if (customMsg) {
          if (typeof customMsg === 'string' && customMsg.includes('Pipeline failed after 4 attempts')) {
            errorMessage = "The AI couldn't generate a stable solution for this problem. Please refine the problem statement.";
          } else {
            errorMessage = `${customMsg} Please try again!`;
          }
        } else if (status >= 500) {
          errorMessage = 'Hmm, looks like we hit a snag!  Don\'t worry, just hit "Try Again" and we\'ll give it another go!';
        } else if (status === 404) {
          errorMessage = 'Can\'t find what we\'re looking for right now. Check your connection and try again! 🌐';
        } else if (status === 403 || status === 401) {
          errorMessage = 'Access denied! Please check your permissions and try again.';
        } else {
          errorMessage = 'Oops! Something unexpected happened. Let\'s try that again!';
        }
      } else if (err.message) {
        // Handle network errors or other non-HTTP errors
        if (err.message.includes('Network Error') || err.message.includes('timeout')) {
          errorMessage = 'Connection hiccup detected! Check your internet and try again!';
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          errorMessage = 'Hmm, looks like we hit a snag! Don\'t worry, just hit "Try Again" and we\'ll give it another go!';
        } else {
          // Only use the error message if it's user-friendly
          const msg = err.message.trim();
          if (msg && !msg.includes('500') && !msg.includes('Internal Server Error') && !msg.toLowerCase().includes('server')) {
            errorMessage = `${msg} Please try again!`;
          }
        }
      }

      setError(errorMessage);
      console.error('Error generating solution:', err);
    } finally {
      if (randomMessageIntervalRef.current) {
        clearInterval(randomMessageIntervalRef.current);
        randomMessageIntervalRef.current = null;
      }
      setIsLoading(false);
      setLoadingMessage('');
      setRandomMessage('');
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchSolution(controller.signal);

    return () => {
      controller.abort();
      if (randomMessageIntervalRef.current) {
        clearInterval(randomMessageIntervalRef.current);
        randomMessageIntervalRef.current = null;
      }
    };
  }, [problemStatement, language, JSON.stringify(testCases), userId]);

  // The AI-assist endpoint often leaves "output" blank for cases it expects the instructor to
  // confirm by running them. Pre-fill from the actual run result as a convenience — a blank
  // result is left as blank (it may be the correct answer, e.g. reversing an empty string).
  const fillBlankExpectedOutputs = (testResultsArray) => {
    setSolutionData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        test_cases: prev.test_cases.map((tc, index) => {
          const result = testResultsArray[index];
          if (tc.output?.trim() || !result || result.error) return tc;
          return { ...tc, output: result.actualOutput || '' };
        }),
      };
    });
  };

  const handleExpectedOutputChange = (index, value) => {
    setSolutionData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        test_cases: prev.test_cases.map((tc, i) =>
          i === index ? { ...tc, output: value } : tc
        ),
      };
    });
  };

  const handleTest = async () => {
    if (!solutionData || !solutionData.solution || !solutionData.test_cases.length) {
      setError('No solution or test cases available to test. Please generate a solution first! 🎯');
      return;
    }

    setIsTesting(true);
    setError('');
    setTestResults([]);
    setLoadingMessage('Testing...');

    try {
      if (language.toLowerCase() === 'mysql') {
        // MySQL testing logic
        let dbName;
        try {
          setLoadingMessage('Initializing database session...');
          // Start session
          const startPayload = { userId, action: 'start' };
          if (fileUrl) {
            startPayload.schema = fileUrl; // Schema is optional
          }
          const startResponse = await dbEngine(startPayload);
          console.log('dbEngine start response:', startResponse);

          if (startResponse.status !== 'success' || !startResponse.db_name) {
            throw new Error(`Failed to start MySQL session: ${startResponse.message || 'No db_name returned.'}`);
          }
          dbName = startResponse.db_name;

          // Execute queries for each test case
          const testResultsArray = [];
          for (let index = 0; index < solutionData.test_cases.length; index++) {
            const testCase = solutionData.test_cases[index];
            setLoadingMessage(`Testing case ${index + 1} of ${solutionData.test_cases.length}...`);

            const query = testCase.input ? `${testCase.input}; ${solutionData.solution.trim()}` : solutionData.solution.trim();
            const queryResponse = await dbEngine({
              userId,
              query,
              action: 'query',
              db_name: dbName
            });

            const actualOutput = typeof queryResponse.result === 'string'
              ? queryResponse.result.trim()
              : JSON.stringify(queryResponse.result).trim();
            const expectedOutput = testCase.output?.trim() || '';
            const passed = actualOutput === expectedOutput;

            testResultsArray.push({
              testCaseIndex: index,
              input: testCase.input,
              expectedOutput,
              actualOutput,
              passed,
              stderr: queryResponse.error || '',
            });
          }

          setTestResults(testResultsArray);
          fillBlankExpectedOutputs(testResultsArray);
        } finally {
          // End session
          if (dbName) {
            await dbEngine({ userId, action: 'end', db_name: dbName });
          }
        }
      } else {
        // Non-MySQL testing logic - matching CodeEditor.js payload structure
        const normalizedLanguage = getNormalizedLanguage(language);
        const executionLanguage = normalizedLanguage === 'cpp' ? 'g++' :
          normalizedLanguage === 'c' ? 'c' :
            normalizedLanguage === 'csharp' ? 'c#' :
              normalizedLanguage;

        const fileName = normalizedLanguage === 'cpp' ? 'main.cpp' :
          normalizedLanguage === 'c' ? 'main.c' :
            normalizedLanguage === 'java' ? 'Main.java' :
              normalizedLanguage === 'csharp' ? 'Program.cs' :
                `main${languageOptions[normalizedLanguage]?.fileExtension || ".py"}`;

        const basePayload = {
          language: executionLanguage,
          version: version || defaultVersions[normalizedLanguage] || "10.2.0",
          files: [
            {
              name: fileName,
              content: mergeSolutionAndHarness(solutionData.solution, solutionData.harness, normalizedLanguage)
            },
          ],
          contentId: "",
          courseId: "",
          sectionId: "",
          userId: userId || "",
          stdin: "",
        };

        // Test each test case sequentially to show progress
        const testResultsArray = [];
        for (let index = 0; index < solutionData.test_cases.length; index++) {
          const testCase = solutionData.test_cases[index];
          setLoadingMessage(`Testing case ${index + 1} of ${solutionData.test_cases.length}...`);

          const payload = {
            ...basePayload,
            stdin: testCase.input || ''
          };

          console.log('executeCode payload:', payload);

          try {
            const result = await executeCode(payload);

            if (!result?.run || typeof result.run.code === 'undefined') {
              throw new Error("Invalid response format: Missing 'run' or 'code' property.");
            }

            const { code: executionCode, stdout = '', stderr = '' } = result.run;
            const testError = stderr ? stderr.trim() : stdout && executionCode !== 0 ? stdout.trim() : '';

            if (executionCode !== 0) {
              testResultsArray.push({
                testCaseIndex: index,
                input: testCase.input || '',
                expectedOutput: testCase.output?.trim() || '',
                actualOutput: testError,
                passed: false,
                stderr: testError,
                error: true
              });
              continue;
            }

            const gotOutput = stdout.trim();
            const expectedOutput = testCase.output?.trim() || '';
            let passed = gotOutput === expectedOutput;
            if (normalizedLanguage === 'csharp') {
              try {
                const parsedGot = JSON.parse(gotOutput);
                const parsedExpected = JSON.parse(expectedOutput);
                passed = JSON.stringify(parsedGot) === JSON.stringify(parsedExpected);
              } catch (e) {
                passed = gotOutput === expectedOutput;
              }
            }

            testResultsArray.push({
              testCaseIndex: index,
              input: testCase.input || '',
              expectedOutput,
              actualOutput: gotOutput,
              passed,
              stderr: '',
              error: false
            });
          } catch (testErr) {
            // Extract user-friendly error message
            let errorMessage = 'Test execution failed. Please try again! 🔄';

            if (testErr.response) {
              const status = testErr.response.status;
              if (status >= 500) {
                errorMessage = 'Oops! Something went wrong during test execution. Try again! 🎯';
              } else {
                const customMsg = testErr.response.data?.error ||
                  testErr.response.data?.message ||
                  testErr.response.data?.detail;
                if (customMsg && !customMsg.toLowerCase().includes('server') && !customMsg.includes('500')) {
                  errorMessage = `${customMsg} Please try again!`;
                } else {
                  errorMessage = 'Test execution failed. Please try again! 🔄';
                }
              }
            } else if (testErr.message) {
              const msg = testErr.message.trim();
              if (msg && !msg.includes('500') && !msg.includes('Internal Server Error') && !msg.toLowerCase().includes('server')) {
                errorMessage = `${msg} Please try again!`;
              }
            }

            testResultsArray.push({
              testCaseIndex: index,
              input: testCase.input || '',
              expectedOutput: testCase.output?.trim() || '',
              actualOutput: '',
              passed: false,
              stderr: errorMessage,
              error: true
            });
          }
        }

        setTestResults(testResultsArray);
        fillBlankExpectedOutputs(testResultsArray);
      }
    } catch (err) {
      // Extract user-friendly error message
      let errorMessage = 'Oops! Something went wrong while running the tests. Give it another shot! 🚀';

      if (err.response) {
        const status = err.response.status;
        if (status >= 500) {
          errorMessage = 'Hmm, looks like we hit a snag while testing! 🤔 Don\'t worry, just try again!';
        } else if (status === 404) {
          errorMessage = 'Can\'t find what we need right now. Check your connection and try again! 🌐';
        } else if (status >= 400) {
          const customMsg = err.response.data?.error ||
            err.response.data?.message ||
            err.response.data?.detail;
          if (customMsg && !customMsg.toLowerCase().includes('server') && !customMsg.includes('500')) {
            errorMessage = `${customMsg} Please try again!`;
          } else {
            errorMessage = 'Something\'s not quite right with the test cases. Double-check and try again! ✨';
          }
        } else {
          const customMsg = err.response.data?.error ||
            err.response.data?.message ||
            err.response.data?.detail;
          if (customMsg && !customMsg.toLowerCase().includes('server') && !customMsg.includes('500')) {
            errorMessage = `${customMsg} Please try again!`;
          } else {
            errorMessage = 'Oops! Something unexpected happened during testing. Let\'s try that again! 🎯';
          }
        }
      } else if (err.message) {
        if (err.message.includes('Network Error') || err.message.includes('timeout')) {
          errorMessage = 'Connection hiccup detected! 📡 Check your internet and try again!';
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          errorMessage = 'Hmm, looks like we hit a snag while testing! 🤔 Don\'t worry, just try again!';
        } else {
          const msg = err.message.trim();
          if (msg && !msg.includes('500') && !msg.includes('Internal Server Error') && !msg.toLowerCase().includes('server')) {
            errorMessage = `${msg} Please try again!`;
          }
        }
      }

      setError(errorMessage);
      console.error('Error executing test cases:', err);
    } finally {
      setIsTesting(false);
      setLoadingMessage('');
    }
  };

  const handleAccept = () => {
    if (solutionData) {
      if (solutionData.schema && !schemaFileName.trim()) {
        showToast('Please enter a schema file name before accepting (e.g. course_basic.sql)', 'error');
        return;
      }
      onAccept({
        solution: solutionData.solution,
        harness: solutionData.harness,
        schema: solutionData.schema,
        schemaFileName: schemaFileName.trim(),
        testCases: solutionData.test_cases.map(tc => ({
          testInput: tc.input,
          expectedOutput: tc.output
        }))
      });
    } else {
      showToast('No solution available to accept. Please generate a solution first! ✨', 'error');
    }
  };

  const handleRegenerate = () => {
    fetchSolution();
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="modal-90w"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Generated Solution</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '50vh' }}>
            <Spinner animation="border" style={{ color: '#23479f', width: '3rem', height: '3rem' }} />
            <span className="mt-3" style={{ color: '#23479f', fontSize: '1.2rem', fontWeight: 500 }}>
              {loadingMessage || 'Generating solution...'}
            </span>
            {randomMessage && (
              <span className="mt-2" style={{ color: '#6c757d', fontSize: '0.95rem', fontStyle: 'italic', textAlign: 'center', maxWidth: '500px' }}>
                {randomMessage}
              </span>
            )}
          </div>
        ) : isTesting ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '50vh' }}>
            <Spinner animation="border" style={{ color: '#23479f', width: '3rem', height: '3rem' }} />
            <span className="mt-3" style={{ color: '#23479f', fontSize: '1.2rem', fontWeight: 500 }}>
              {loadingMessage || 'Testing solution...'}
            </span>
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <div className="text-danger mb-4" style={{ fontSize: '1.25rem', fontWeight: '500' }}>
              Solution and Test Cases Generation Failed
            </div>
            <Button
              onClick={handleRegenerate}
              style={{ backgroundColor: '#23479f', borderColor: '#23479f', padding: '10px 24px', fontSize: '1rem', borderRadius: '5px' }}
            >
              Try Again
            </Button>
          </div>
        ) : solutionData ? (
          <div>
            <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
              <Card.Header>
                <h5 className="mb-0">Problem Statement</h5>
              </Card.Header>
              <Card.Body>
                <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>{problemStatement}</p>
              </Card.Body>
            </Card>
            <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
              <Card.Header>
                <h5 className="mb-0">Solution ({language})</h5>
              </Card.Header>
              <Card.Body>
                <pre style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                  {solutionData.solution || 'No solution code provided.'}
                </pre>
              </Card.Body>
            </Card>
            {solutionData.schema && (() => {
              const { tables, inserts, raw } = parseSchema(solutionData.schema);
              return (
                <div className="mb-3">
                  <h5 className="mb-3 fw-bold" style={{ color: '#23479f' }}>Database Schema</h5>

                  {tables.length > 0 ? (
                    tables.map((t, idx) => (
                      <Card key={idx} className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
                        <Card.Header style={{ backgroundColor: '#23479f', color: '#fff' }}>
                          <h6 className="mb-0 fw-bold">📋 Table: {t.name}</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <Table striped bordered hover responsive className="mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th>Field / Column</th>
                                <th>Type</th>
                                <th>Key / Constraints</th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.columns.map((col, cIdx) => (
                                <tr key={cIdx}>
                                  <td><strong>{col.name}</strong></td>
                                  <td><code>{col.type}</code></td>
                                  <td>
                                    {col.isPrimary && <span className="me-1 text-white" style={{ backgroundColor: '#23479f', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block' }}>PRIMARY KEY</span>}
                                    {col.isForeign && <span className="me-1" style={{ border: '1px solid #23479f', color: '#23479f', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block' }}>FOREIGN KEY ({col.foreignRef})</span>}
                                    {col.otherConstraints && <span className="text-muted">{col.otherConstraints}</span>}
                                    {!col.isPrimary && !col.isForeign && !col.otherConstraints && <span className="text-muted">-</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
                      <Card.Body>
                        <pre style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                          {raw}
                        </pre>
                      </Card.Body>
                    </Card>
                  )}

                  {inserts.length > 0 && (
                    <div className="mt-4">
                      <h5 className="mb-3 fw-bold" style={{ color: '#23479f' }}>Initial Seed Data (Table Grids)</h5>
                      {inserts.map((ins, idx) => (
                        <Card key={idx} className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
                          <Card.Header style={{ backgroundColor: '#23479f', color: '#fff' }}>
                            <h6 className="mb-0 fw-bold">Rows for Table: {ins.table}</h6>
                          </Card.Header>
                          <Card.Body className="p-0">
                            <Table striped bordered hover responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
                              <thead className="table-light">
                                <tr>
                                  {ins.columns.length > 0 ? (
                                    ins.columns.map((col, hIdx) => <th key={hIdx}>{col}</th>)
                                  ) : (
                                    ins.rows[0] && ins.rows[0].map((_, hIdx) => <th key={hIdx}>Col {hIdx + 1}</th>)
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {ins.rows.map((row, rIdx) => (
                                  <tr key={rIdx}>
                                    {row.map((val, vIdx) => (
                                      <td key={vIdx}>{val}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}

                  <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Save Schema File Name: <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. course_basic.sql"
                          value={schemaFileName}
                          onChange={(e) => setSchemaFileName(e.target.value)}
                          style={{ borderColor: '#23479f' }}
                        />
                        <Form.Text className="text-muted">
                          This SQL schema will be automatically staged as a SQL file. Click Accept to confirm.
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </div>
              );
            })()}
            {/* {(solutionData.harness || solutionData.starter_code) && (
              <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
                <Card.Header style={{ padding: 0, background: '#1e1e2e', borderRadius: '5px 5px 0 0' }}>
                  <div style={{ display: 'flex', borderBottom: '1px solid #3a3a5c' }}>
                    {solutionData.starter_code && (
                      <button
                        onClick={() => setHarnessTab('starter')}
                        style={{
                          padding: '10px 20px',
                          background: 'none',
                          border: 'none',
                          borderBottom: harnessTab === 'starter' ? '2px solid #23479f' : '2px solid transparent',
                          color: harnessTab === 'starter' ? '#fff' : '#8b949e',
                          fontWeight: harnessTab === 'starter' ? 600 : 400,
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          transition: 'all 0.2s',
                        }}
                      >
                        📄 Starter Code
                      </button>
                    )} */}
            {/* {solutionData.harness && (
              <button
                onClick={() => setHarnessTab('harness')}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: harnessTab === 'harness' ? '2px solid #23479f' : '2px solid transparent',
                  color: harnessTab === 'harness' ? '#fff' : '#8b949e',
                  fontWeight: harnessTab === 'harness' ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s',
                }}
              >
                🧪 Test Harness
              </button>
            )}
          </div>
                </Card.Header>
      <Card.Body style={{ padding: 0, background: '#1e1e2e', borderRadius: '0 0 5px 5px' }}>
        <pre style={{
          margin: 0,
          padding: '16px',
          background: 'transparent',
          color: '#cdd9e5',
          fontSize: '0.9rem',
          whiteSpace: 'pre-wrap',
          fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
          lineHeight: '1.6',
          minHeight: '120px',
        }}>
          {harnessTab === 'starter'
            ? (solutionData.starter_code || '')
            : (solutionData.harness || '')}
        </pre>
      </Card.Body>
    </Card>
  )
} */}
            <Card className="mb-3 shadow-sm" style={{ borderColor: '#23479f' }}>
              <Card.Header>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">Test Cases</h5>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="test-cases-tooltip" style={{ maxWidth: '280px' }}>
                        These are <strong>Generated test cases</strong>. Please test them against our integrated compiler using the <strong>Test</strong> button, then click <strong>Accept</strong> to save the solution.
                      </Tooltip>
                    }
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: '#23479f',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        flexShrink: 0,
                        userSelect: 'none',
                      }}
                    >
                      i
                    </span>
                  </OverlayTrigger>
                </div>
              </Card.Header>
              <Card.Body>
                {solutionData.test_cases.length > 0 ? (
                  solutionData.test_cases.map((tc, index) => (
                    <div key={index} className="mb-3" style={{ border: '1px solid #23479f', padding: '10px', borderRadius: '5px' }}>
                      <p className="mb-1">
                        <strong>Input:</strong> {tc.input ? tc.input : <span className="text-muted">(empty string)</span>}
                      </p>
                      <Form.Group className="mb-1">
                        <Form.Label className="mb-1"><strong>Expected Output:</strong></Form.Label>
                        <Form.Control
                          type="text"
                          value={tc.output || ''}
                          placeholder="(empty string) — leave blank if that's the correct output"
                          onChange={(e) => handleExpectedOutputChange(index, e.target.value)}
                        />
                      </Form.Group>
                      {testResults[index] && (
                        <>
                          <p className="mb-1">
                            <strong>Actual Output:</strong> {testResults[index].actualOutput ? testResults[index].actualOutput : <span className="text-muted">(empty string)</span>}
                          </p>
                          {testResults[index].stderr && (
                            <p className="mb-1 text-danger"><strong>Error:</strong> {testResults[index].stderr}</p>
                          )}
                          <p className="mb-0" style={{ color: testResults[index].passed ? '#28a745' : '#dc3545' }}>
                            <strong>Status:</strong> {testResults[index].passed ? 'Passed' : 'Failed'}
                          </p>
                          {tc.output !== testResults[index].actualOutput && !testResults[index].error && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="mt-1"
                              onClick={() => handleExpectedOutputChange(index, testResults[index].actualOutput)}
                            >
                              Use Actual Output as Expected
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No test cases available.</p>
                )}
              </Card.Body>
            </Card>
          </div >
        ) : (
          <div className="text-center">
            <div className="text-muted" style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
              No solution generated yet. Give it another try! 🚀
            </div>
            <Button
              onClick={handleRegenerate}
              style={{ backgroundColor: '#23479f', borderColor: '#23479f' }}
            >
              Try Again
            </Button>
          </div>
        )}
      </Modal.Body >
      <Modal.Footer style={{ borderTop: '1px solid #23479f' }}>
        {language?.toLowerCase() !== 'mysql' && (
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={isLoading || isTesting || !solutionData || !solutionData.test_cases.length}
            style={{ backgroundColor: '#6c757d', borderColor: '#6c757d', marginRight: '10px' }}
          >
            {isTesting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {loadingMessage || 'Testing...'}
              </>
            ) : 'Test'}
          </Button>
        )}
        <Button
          onClick={handleAccept}
          disabled={isLoading || isTesting || !solutionData}
          style={{ backgroundColor: '#23479f', borderColor: '#23479f', marginRight: '10px' }}
        >
          Accept
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onClose}
          style={{ borderColor: '#23479f', color: '#23479f' }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal >
  );
};

export default CodeSolutionGenerator;